import { Article } from "@/types/article";
import { Button } from "@/components/ui/button";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, BookOpen, Users, PenTool, Star } from "lucide-react";
import { useParallax } from "@/hooks/useParallax";

interface ArticlesSectionProps {
  articles: Article[] | undefined;
  isLoading: boolean;
}

// Broken Lines Layout (Lv4) - 斜めラインを作る段階的オフセット
// "深夜の街灯がバラバラに落ちるような配置"

// FEATURED: Lv4 フル崩し - 強い斜めライン
const featuredBrokenLines = [
  { col: "md:col-span-2", row: "md:row-span-2", x: 0, y: 0, parallaxSpeed: 0.04 },
  { col: "md:col-span-1", row: "md:row-span-1", x: 6, y: 12, parallaxSpeed: 0.03 },
  { col: "md:col-span-1", row: "md:row-span-1", x: -4, y: 20, parallaxSpeed: 0.05 },
];

// INTERVIEW: Lv3-Lv4 混合 - 気配程度の斜めライン
const interviewBrokenLines = [
  { x: 0, y: 0, parallaxSpeed: 0.025 },
  { x: 4, y: 5, parallaxSpeed: 0.035 },
  { x: 8, y: 3, parallaxSpeed: 0.03 },
  { x: 2, y: 10, parallaxSpeed: 0.02 },
  { x: 6, y: 7, parallaxSpeed: 0.04 },
  { x: -2, y: 12, parallaxSpeed: 0.025 },
];

// BLOG: Lv2-Lv3 - 読みやすさ重視の控えめな崩し
const blogBrokenLines = [
  { x: 0, y: 0, parallaxSpeed: 0.02 },
  { x: 2, y: 3, parallaxSpeed: 0.025 },
  { x: 4, y: 1, parallaxSpeed: 0.03 },
  { x: 0, y: 5, parallaxSpeed: 0.02 },
  { x: 3, y: 2, parallaxSpeed: 0.025 },
  { x: -1, y: 4, parallaxSpeed: 0.02 },
];

const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'ARTICLES':
      return <BookOpen className="w-3 h-3" />;
    case 'INTERVIEW':
      return <Users className="w-3 h-3" />;
    case 'BLOG':
      return <PenTool className="w-3 h-3" />;
    default:
      return <Star className="w-3 h-3" />;
  }
};

// Broken Lines用スタイル型
interface BrokenLineStyle {
  col?: string;
  row?: string;
  x: number;
  y: number;
  parallaxSpeed: number;
}

// グラスモーフィズムカード with Broken Lines parallax
const GlassCard = ({ 
  article, 
  brokenLine,
  isFeatured = false,
  parallaxOffset = 0,
  isMobile = false,
}: { 
  article: Article; 
  brokenLine: BrokenLineStyle;
  isFeatured?: boolean;
  parallaxOffset?: number;
  isMobile?: boolean;
}) => {
  // Broken Lines: 斜めラインを作るtranslateX/translateY
  const translateX = isMobile ? 0 : brokenLine.x;
  const translateY = isMobile ? 0 : brokenLine.y + parallaxOffset;
  
  return (
    <Link
      to={`/articles/${article.id}`}
      className={`
        group block
        ${brokenLine.col || ''} ${brokenLine.row || ''}
        bg-[rgba(8,20,20,0.55)] backdrop-blur-md
        border border-[rgba(255,255,255,0.05)]
        hover:border-[rgba(255,255,255,0.12)] hover:bg-[rgba(8,20,20,0.62)]
        transition-all duration-600 ease-out
        ${isFeatured ? 'p-6 md:p-8' : 'p-5 md:p-6'}
      `}
      style={{
        transform: `translate(${translateX}px, ${translateY}px)`,
        transitionProperty: 'transform, border-color, background-color, box-shadow',
        transitionDuration: '0.6s',
        transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      {article.image_url && isFeatured && (
        <div className="relative h-32 md:h-48 -mx-6 md:-mx-8 -mt-6 md:-mt-8 mb-4 md:mb-6 overflow-hidden">
          <img
            src={article.image_url}
            alt={article.title}
            className="w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[rgba(8,20,20,0.9)] to-transparent" />
        </div>
      )}
      
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-[#7BCBC2]/70 text-xs tracking-wider">
          {getCategoryIcon(article.category)}
          <span>{article.category}</span>
        </div>
        
        <h3 className={`
          text-[#D3EDE9] font-medium leading-snug tracking-wide
          group-hover:text-[#D3EDE9] group-hover:tracking-wider
          transition-all duration-700 ease-out
          ${isFeatured ? 'text-lg md:text-xl' : 'text-sm md:text-base'}
        `}>
          {article.title}
        </h3>
        
        <p className={`
          text-[#7BCBC2]/50 leading-relaxed
          ${isFeatured ? 'text-sm line-clamp-3' : 'text-xs line-clamp-2'}
        `}>
          {article.excerpt}
        </p>
        
        <div className="flex items-center gap-1 text-[#7BCBC2]/40 text-xs group-hover:text-[#7BCBC2]/60 transition-all duration-600">
          <span className="relative">
            詳しく見る
            <span className="absolute -bottom-0.5 left-0 w-0 h-px bg-[#7BCBC2]/30 group-hover:w-full transition-all duration-800 ease-out" />
          </span>
          <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform duration-600 ease-out" />
        </div>
      </div>
    </Link>
  );
};

const ArticlesSection = ({ articles, isLoading }: ArticlesSectionProps) => {
  const navigate = useNavigate();
  const { scrollY, isMobile, getParallaxOffset } = useParallax({ 
    maxOffset: 20, 
    mobileReduction: 0.3 
  });

  // カテゴリ別に分類
  const featuredArticles = articles?.filter(a => a.category === 'ARTICLES').slice(0, 3) || [];
  const interviewArticles = articles?.filter(a => a.category === 'INTERVIEW').slice(0, 3) || [];
  const blogArticles = articles?.filter(a => a.category === 'BLOG').slice(0, 3) || [];

  const handleViewMore = (category: string) => {
    if (category === "ARTICLES") {
      navigate("/articles-list");
    } else if (category === "INTERVIEW") {
      navigate("/interview-list");
    } else if (category === "BLOG") {
      navigate("/blog-list");
    } else {
      navigate("/recent-posts");
    }
  };

  // Background texture parallax (very subtle)
  const bgParallaxOffset = getParallaxOffset(0.015);

  if (isLoading) {
    return (
      <section className="py-24 md:py-36 bg-[#071B19] relative overflow-hidden">
        <div className="container px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="h-48 bg-[rgba(8,20,20,0.55)] border border-[rgba(255,255,255,0.08)] animate-pulse"
              />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 md:py-36 bg-[#071B19] relative overflow-hidden">
      {/* Background texture with subtle parallax */}
      <div 
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          transform: `translateY(${bgParallaxOffset}px)`,
          transition: 'transform 0.3s ease-out',
        }}
      />
      
      <div className="container px-4 relative z-10">
        
        {/* FEATURED / ARTICLES セクション - 強めのブロークングリッド + パララックス */}
        {featuredArticles.length > 0 && (
          <div className="mb-20 md:mb-32">
            <div className="mb-8 md:mb-12 md:ml-4 group/header">
              <h2 className="text-2xl md:text-3xl font-light text-[#D3EDE9] tracking-[0.25em] mb-2 breathing-text">
                FEATURED
              </h2>
              <p className="text-xs text-[#7BCBC2]/50 tracking-wide ml-1 relative inline-block">
                注目の記事
                <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#7BCBC2]/30 group-hover/header:w-full transition-all duration-800 ease-out" />
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {featuredArticles.map((article, index) => {
                const brokenLine = featuredBrokenLines[index] || featuredBrokenLines[0];
                const cardParallax = getParallaxOffset(brokenLine.parallaxSpeed);
                return (
                  <GlassCard
                    key={article.id}
                    article={article}
                    brokenLine={brokenLine}
                    isFeatured={index === 0}
                    parallaxOffset={cardParallax}
                    isMobile={isMobile}
                  />
                );
              })}
            </div>
            
            {featuredArticles.length >= 3 && (
              <div className="flex justify-end mt-8 md:mt-10">
                <Button
                  onClick={() => handleViewMore("ARTICLES")}
                  variant="ghost"
                  size="sm"
                  className="text-[#7BCBC2]/60 hover:text-[#A9D1CA] hover:bg-transparent text-xs tracking-wider"
                >
                  すべて見る →
                </Button>
              </div>
            )}
          </div>
        )}

        {/* INTERVIEW セクション - 非表示 */}
        {/* BLOG セクション - 非表示 */}

        {/* 記事がない場合 */}
        {featuredArticles.length === 0 && interviewArticles.length === 0 && blogArticles.length === 0 && (
          <div className="text-center py-16">
            <p className="text-[#7BCBC2]/40 text-sm">
              記事はまだありません。
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default ArticlesSection;